import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { DEFAULT_THEME_ID, getTheme, THEMES, THEME_STORAGE_KEY } from '@/theme/themes';

const ThemeContext = createContext(null);

function applyAccentTheme(id) {
  const root = document.documentElement;
  THEMES.forEach(t => root.classList.remove(`theme-${t.id}`));
  root.classList.add(`theme-${id}`);
}

export function ThemeProvider({ children }) {
  // Always dark mode — no toggle
  const theme = 'dark';

  const [accentTheme, setAccentThemeState] = useState(() => {
    const saved = typeof localStorage !== 'undefined' ? localStorage.getItem(THEME_STORAGE_KEY) : null;
    return THEMES.some(t => t.id === saved) ? saved : DEFAULT_THEME_ID;
  });

  useEffect(() => {
    const root = document.documentElement;
    root.classList.add('dark');
    root.classList.remove('light');
    localStorage.setItem('bodymax-theme', 'dark');
  }, []);

  useEffect(() => {
    applyAccentTheme(accentTheme);
    localStorage.setItem(THEME_STORAGE_KEY, accentTheme);
  }, [accentTheme]);

  const setAccentTheme = useCallback((id) => {
    if (THEMES.some(t => t.id === id)) setAccentThemeState(id);
  }, []);

  const value = useMemo(() => ({
    theme,
    setTheme: () => {},
    toggleTheme: () => {},
    accentTheme,
    setAccentTheme,
    themes: THEMES,
    activeTheme: getTheme(accentTheme),
  }), [accentTheme, setAccentTheme]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
};
